/**
 * LoadingScene — 미니게임 전환 시 짧은 splash
 * 핑크 그라데이션 배경 + 로고 + 캐릭터 + "준비 중..." 텍스트
 * 약 0.8초 후 다음 scene으로 자동 전환
 *
 * 사용법:
 *   this.scene.start('LoadingScene', { nextScene: 'MiniGameHubScene' });
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';

export class LoadingScene extends Phaser.Scene {
  constructor() { super('LoadingScene'); }

  create(data: { nextScene?: string }): void {
    const nextScene = data?.nextScene ?? 'MiniGameHubScene';

    // Gradient background using stacked rectangles (Phaser doesn't natively support gradients)
    const colors = [0xffcce0, 0xff9fbf, 0xff6fa1, 0xe54e87, 0xc0336a];
    const sliceH = GAME_HEIGHT / colors.length;
    colors.forEach((c, i) => {
      this.add.rectangle(GAME_WIDTH / 2, i * sliceH + sliceH / 2, GAME_WIDTH, sliceH, c);
    });

    // Soft overlay
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.18);

    // Logo text
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80, '👗 로하의 옷장', {
      fontFamily: Theme.font.family,
      fontSize: '52px',
      fontStyle: '900',
      color: '#ffffff',
      stroke: '#c0336a',
      strokeThickness: 3,
    }).setOrigin(0.5).setAlpha(0);

    const logo = this.children.getAt(this.children.length - 1) as Phaser.GameObjects.Text;
    this.tweens.add({ targets: logo, alpha: 1, duration: 250, ease: 'Sine.Out' });

    // Subtitle
    const sub = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '✨ 준비 중...', {
      fontFamily: Theme.font.family,
      fontSize: '22px',
      fontStyle: '700',
      color: '#fff0f8',
    }).setOrigin(0.5).setAlpha(0);
    this.tweens.add({ targets: sub, alpha: 1, duration: 300, delay: 150 });

    // Spinning sparkle decoration
    const sparkles: Phaser.GameObjects.Text[] = [];
    const sparkPositions = [
      [GAME_WIDTH / 2 - 200, GAME_HEIGHT / 2 - 120],
      [GAME_WIDTH / 2 + 200, GAME_HEIGHT / 2 - 120],
      [GAME_WIDTH / 2 - 260, GAME_HEIGHT / 2 + 20],
      [GAME_WIDTH / 2 + 260, GAME_HEIGHT / 2 + 20],
    ];
    sparkPositions.forEach(([x, y], i) => {
      const sp = this.add.text(x, y, '✨', { fontSize: '28px' }).setOrigin(0.5).setAlpha(0.7);
      sparkles.push(sp);
      this.tweens.add({
        targets: sp, angle: 360, duration: 1200,
        delay: i * 100, repeat: -1, ease: 'Linear',
      });
    });

    // Fade out and switch after 800ms
    this.time.delayedCall(650, () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(nextScene);
      });
    });

    this.cameras.main.fadeIn(180);
  }
}
